import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Filter, Download, ChevronDown, ChevronRight, User, 
  MapPin, BookOpen, Users, GraduationCap, FileText, X, ZoomIn,
  Loader2, RefreshCw, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/api/api';
import * as XLSX from 'xlsx';
import Pagination from '@/components/Pagination';

// Document thumbnail component - defined outside main component
const DocumentThumbnail = ({ doc, label, onZoom }) => {
  if (!doc) return null;
  
  const isImage = doc.startsWith('data:image') || doc.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPdf = doc.startsWith('data:application/pdf') || doc.match(/\.pdf$/i);
  
  // Determine source badge color
  const getSourceBadge = (source, status) => {
    if (source === 'portal') {
      return (
        <span className={`absolute top-1 left-1 text-[8px] px-1 py-0.5 rounded ${
          status === 'verified' ? 'bg-green-500 text-white' :
          status === 'rejected' ? 'bg-red-500 text-white' :
          'bg-purple-500 text-white'
        }`}>
          {status === 'verified' ? '✓ Portal' : status === 'rejected' ? '✗ Portal' : 'Portal'}
        </span>
      );
    } else if (source === 'admission') {
      return <span className="absolute top-1 left-1 text-[8px] px-1 py-0.5 rounded bg-blue-500 text-white">Admission</span>;
    } else if (source === 'online') {
      return <span className="absolute top-1 left-1 text-[8px] px-1 py-0.5 rounded bg-orange-500 text-white">Online</span>;
    }
    return null;
  };
  
  return (
    <div 
      className="relative group cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 transition-all"
      onClick={() => onZoom({ url: doc, label, isImage, isPdf })}
    >
      <div className="w-20 h-20 bg-gray-100 flex items-center justify-center">
        {isImage ? (
          <img src={doc} alt={label} className="w-full h-full object-cover" />
        ) : isPdf ? (
          <FileText className="w-8 h-8 text-red-500" />
        ) : (
          <FileText className="w-8 h-8 text-gray-400" />
        )}
      </div>
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <ZoomIn className="w-6 h-6 text-white" />
      </div>
      <p className="text-xs text-center py-1 bg-gray-50 truncate px-1">{label}</p>
    </div>
  );
};

// Enhanced Document thumbnail with source badge
const DocumentThumbnailWithSource = ({ doc, onZoom }) => {
  if (!doc || !doc.url) return null;
  
  const isImage = doc.url.startsWith('data:image') || doc.url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPdf = doc.url.startsWith('data:application/pdf') || doc.url.match(/\.pdf$/i);
  
  // Determine source badge color
  const getSourceColor = () => {
    if (doc.source === 'portal') {
      if (doc.status === 'verified') return 'bg-green-500';
      if (doc.status === 'rejected') return 'bg-red-500';
      return 'bg-purple-500';
    }
    if (doc.source === 'admission') return 'bg-blue-500';
    if (doc.source === 'online') return 'bg-orange-500';
    if (doc.source === 'legacy') return 'bg-gray-500';
    return 'bg-gray-400';
  };
  
  const getSourceLabel = () => {
    if (doc.source === 'portal') {
      if (doc.status === 'verified') return '✓';
      if (doc.status === 'rejected') return '✗';
      return 'P';
    }
    if (doc.source === 'admission') return 'A';
    if (doc.source === 'online') return 'O';
    if (doc.source === 'legacy') return 'L';
    return '';
  };
  
  return (
    <div 
      className="relative group cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 transition-all"
      onClick={() => onZoom({ url: doc.url, label: doc.label, isImage, isPdf })}
    >
      {/* Source badge */}
      {doc.source && (
        <span className={`absolute top-1 left-1 z-10 text-[10px] w-4 h-4 flex items-center justify-center rounded-full ${getSourceColor()} text-white font-bold`}>
          {getSourceLabel()}
        </span>
      )}
      
      <div className="w-20 h-20 bg-gray-100 flex items-center justify-center">
        {isImage ? (
          <img src={doc.url} alt={doc.label} className="w-full h-full object-cover" />
        ) : isPdf ? (
          <FileText className="w-8 h-8 text-red-500" />
        ) : (
          <FileText className="w-8 h-8 text-gray-400" />
        )}
      </div>
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <ZoomIn className="w-6 h-6 text-white" />
      </div>
      <p className="text-xs text-center py-1 bg-gray-50 truncate px-1" title={doc.label}>
        {doc.label.replace(/ \(.*\)$/, '')}
      </p>
    </div>
  );
};

// Get documents from student data - handles both object and array formats
const getStudentDocuments = (student) => {
  const docs = [];
  let photoUrl = null;
  
  // Helper function to extract URL from document value
  const extractDocUrl = (value) => {
    if (!value) return null;
    // Case 1: Simple string (base64 or URL)
    if (typeof value === 'string') {
      const isBase64 = value.startsWith('data:');
      const isUrl = value.startsWith('http://') || value.startsWith('https://');
      if (isBase64 || isUrl) return value;
    }
    // Case 2: Object with data field (from admission form: { data: 'base64...', filename: '...' })
    if (typeof value === 'object' && value.data) {
      const data = value.data;
      const isBase64 = data.startsWith('data:');
      const isUrl = data.startsWith('http://') || data.startsWith('https://');
      if (isBase64 || isUrl) return data;
    }
    return null;
  };
  
  // Check documents field
  if (student.documents) {
    // Format 1: Object format (from online/admission form: {profile_photo: 'base64...'} or {profile_photo: {data: '...'}})
    if (typeof student.documents === 'object' && !Array.isArray(student.documents)) {
      Object.entries(student.documents).forEach(([key, value]) => {
        const docUrl = extractDocUrl(value);
        if (docUrl) {
          if (key === 'profile_photo' || key === 'photo' || key === 'passport_photo' || key === 'passport_size_photo') {
            photoUrl = docUrl;
          } else {
            const docName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            docs.push({
              url: docUrl,
              label: docName,
              type: key,
              source: 'admission'
            });
          }
        }
      });
    }
    // Format 2: Array format (from student portal: [{type: 'photo', data: 'base64...'}])
    else if (Array.isArray(student.documents) && student.documents.length > 0) {
      student.documents.forEach(doc => {
        if (doc.data || doc.url) {
          const docUrl = doc.data || doc.url;
          if (doc.type === 'photo' || doc.type === 'profile_photo' || doc.type === 'passport_photo' || doc.type === 'passport_size_photo') {
            photoUrl = docUrl;
          } else {
            docs.push({ 
              url: docUrl,
              label: doc.name || doc.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Document',
              type: doc.type 
            });
          }
        }
      });
    }
  }
  
  // Add photo as first document if found
  if (photoUrl) {
    docs.unshift({ url: photoUrl, label: 'Photo', type: 'photo' });
  }
  
  // Fallback: Check direct photo fields (for older records without documents array)
  if (!photoUrl) {
    const directPhoto = student.photo || student.passport_photo || student.photo_url;
    if (directPhoto) {
      docs.unshift({ url: directPhoto, label: 'Photo', type: 'photo' });
    }
  }
  
  // Fallback: Individual document fields (for legacy/migrated data)
  const docFields = [
    { field: 'aadhaar_card', label: 'Aadhaar Card' },
    { field: 'aadhar', label: 'Aadhaar Card' },
    { field: 'class_10_marksheet', label: '10th Marksheet' },
    { field: 'class_10', label: '10th Marksheet' },
    { field: 'class_12_marksheet', label: '12th Marksheet' },
    { field: 'class_12', label: '12th Marksheet' },
    { field: 'transfer_certificate', label: 'Transfer Certificate' },
    { field: 'migration_certificate', label: 'Migration Certificate' },
    { field: 'caste_certificate', label: 'Caste Certificate' },
    { field: 'income_certificate', label: 'Income Certificate' },
    { field: 'domicile_certificate', label: 'Domicile Certificate' },
    { field: 'doc_profile_photo', label: 'Profile Photo' },
    { field: 'doc_signature', label: 'Signature' }
  ];
  
  docFields.forEach(({ field, label }) => {
    // Only add if not already in docs array (avoid duplicates)
    const existingDoc = docs.find(d => d.type === field || d.label === label);
    if (!existingDoc && student[field]) {
      docs.push({ url: student[field], label, type: field, source: 'legacy' });
    }
  });
  
  // Add documents from Student Portal (portal_documents)
  // NOTE: We now show ALL documents with source labels, including duplicates
  if (student.portal_documents && Array.isArray(student.portal_documents)) {
    student.portal_documents.forEach(doc => {
      if (doc.data) {
        if (doc.type === 'photo' || doc.type === 'profile_photo') {
          // For photos, add with portal label if not already has photo
          if (!photoUrl) {
            photoUrl = doc.data;
            docs.unshift({ url: doc.data, label: 'Photo (Portal)', type: 'photo', source: 'portal', status: doc.status });
          } else {
            // If photo already exists, still add portal photo with label
            docs.push({ url: doc.data, label: 'Photo (Portal)', type: 'photo_portal', source: 'portal', status: doc.status });
          }
        } else {
          const docName = doc.name || doc.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Document';
          // Always add portal documents with source label - show duplicates with different sources
          docs.push({
            url: doc.data,
            label: `${docName} (Portal)`,
            type: `${doc.type}_portal`,
            originalType: doc.type,
            source: 'portal',
            status: doc.status,
            uploadedAt: doc.uploaded_at || doc.created_at
          });
        }
      }
    });
  }
  
  // Add source labels to admission documents that don't have them
  docs.forEach(doc => {
    if (!doc.source) {
      doc.source = 'admission';
    }
    // Add source badge to label if not already present
    if (doc.source === 'admission' && !doc.label.includes('(')) {
      doc.label = `${doc.label} (Admission)`;
    }
  });
  
  return docs;
};

// Get student photo from documents or direct fields
const getStudentPhoto = (student) => {
  // Helper function to extract URL from document value
  const extractUrl = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && (value.startsWith('data:') || value.startsWith('http'))) {
      return value;
    }
    if (typeof value === 'object' && value.data) {
      const dataVal = value.data;
      if (typeof dataVal === 'string' && (dataVal.startsWith('data:') || dataVal.startsWith('http'))) {
        return dataVal;
      }
    }
    return null;
  };
  
  // Check documents object format first (most common for new admissions)
  if (student.documents && typeof student.documents === 'object' && !Array.isArray(student.documents)) {
    const photoKeys = ['passport_photo', 'profile_photo', 'photo', 'passport_size_photo'];
    for (const key of photoKeys) {
      const url = extractUrl(student.documents[key]);
      if (url) return url;
    }
  }
  
  // Check documents array format (from student portal)
  if (student.documents && Array.isArray(student.documents)) {
    const photoDoc = student.documents.find(d => 
      d.type === 'photo' || d.type === 'passport_photo' || d.type === 'passport_size_photo' || d.type === 'profile_photo'
    );
    if (photoDoc?.data) return photoDoc.data;
  }
  
  // Fallback to direct fields
  return student.photo || student.passport_photo || student.photo_url || null;
};

// Expanded row content component - defined outside main component
const ExpandedRowContent = ({ student, onZoom }) => {
  const documents = getStudentDocuments(student);
  // Get photo from documents array (first photo type document)
  const photoDoc = documents.find(d => d.type === 'photo' || d.type === 'passport_photo' || d.type === 'profile_photo');
  const studentPhoto = photoDoc?.url || getStudentPhoto(student);
  
  return (
    <tr>
      <td colSpan="10" className="bg-gray-50 p-0">
        <div className="p-6 border-t border-b border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            
            {/* Photo & Basic Info */}
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Photo & ID
              </h4>
              <div className="flex flex-col items-center">
                {studentPhoto ? (
                  <img 
                    src={studentPhoto} 
                    alt="Student" 
                    className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300 cursor-pointer hover:border-blue-400 transition-all"
                    onClick={() => onZoom({ url: studentPhoto, label: 'Photo', isImage: true })}
                  />
                ) : (
                  <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                    <User className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <p className="mt-2 font-bold text-blue-600">{student.student_id || student.admission_number}</p>
                <p className="text-sm text-gray-600">{student.full_name}</p>
              </div>
            </div>

            {/* Personal Information */}
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Personal Info
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Gender:</span>
                  <span className="font-medium">{student.gender || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">DOB:</span>
                  <span className="font-medium">{student.date_of_birth || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Phone:</span>
                  <span className="font-medium">{student.phone || student.mobile || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email:</span>
                  <span className="font-medium text-xs">{student.email || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Blood Group:</span>
                  <span className="font-medium">{student.blood_group || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Religion:</span>
                  <span className="font-medium">{student.religion || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Category:</span>
                  <span className="font-medium">{student.caste_category || student.caste || student.category || '-'}</span>
                </div>
              </div>
            </div>

            {/* Family Information */}
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Family Info
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Father:</span>
                  <span className="font-medium">{student.father_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Father Phone:</span>
                  <span className="font-medium">{student.father_phone || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Father Occ:</span>
                  <span className="font-medium">{student.father_occupation || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Mother:</span>
                  <span className="font-medium">{student.mother_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Mother Phone:</span>
                  <span className="font-medium">{student.mother_phone || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Mother Occ:</span>
                  <span className="font-medium">{student.mother_occupation || '-'}</span>
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <GraduationCap className="w-4 h-4" /> Academic Info
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Course:</span>
                  <span className="font-medium">{student.course_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Session:</span>
                  <span className="font-medium">{student.session || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Admission:</span>
                  <span className="font-medium">{student.admission_date || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Entry Type:</span>
                  <span className="font-medium">{student.is_lateral_entry ? 'Lateral' : 'Regular'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Education Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Class 10th */}
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Class 10th Details
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Board:</span> <span className="font-medium">{student.class_10_board || '-'}</span></div>
                <div><span className="text-gray-500">School:</span> <span className="font-medium">{student.class_10_school || '-'}</span></div>
                <div><span className="text-gray-500">Year:</span> <span className="font-medium">{student.class_10_year || '-'}</span></div>
                <div><span className="text-gray-500">Percentage:</span> <span className="font-medium text-green-600">{student.class_10_percentage || '-'}</span></div>
              </div>
            </div>

            {/* Class 12th */}
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Class 12th Details
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Board:</span> <span className="font-medium">{student.class_12_board || '-'}</span></div>
                <div><span className="text-gray-500">School:</span> <span className="font-medium">{student.class_12_school || '-'}</span></div>
                <div><span className="text-gray-500">Year:</span> <span className="font-medium">{student.class_12_year || '-'}</span></div>
                <div><span className="text-gray-500">Percentage:</span> <span className="font-medium text-green-600">{student.class_12_percentage || '-'}</span></div>
              </div>
            </div>
          </div>

          {/* Consultant Info - Only show if student has consultant */}
          {student.consultant_name && (
            <div className="mt-6 bg-purple-50 rounded-lg p-4 shadow-sm border border-purple-200">
              <h4 className="font-semibold text-purple-700 mb-3 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Consultant Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-purple-600">Name:</span>
                  <span className="font-medium ml-2">{student.consultant_name}</span>
                </div>
                {student.consultant_code && (
                  <div>
                    <span className="text-purple-600">Code:</span>
                    <span className="font-medium ml-2">{student.consultant_code}</span>
                  </div>
                )}
                {student.consultant_phone && (
                  <div>
                    <span className="text-purple-600">Phone:</span>
                    <span className="font-medium ml-2">{student.consultant_phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Address */}
          <div className="mt-6 bg-white rounded-lg p-4 shadow-sm border">
            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Address
            </h4>
            <p className="text-sm text-gray-700">
              {student.address || student.present_address || '-'}
              {student.city && `, ${student.city}`}
              {student.district && `, ${student.district}`}
              {student.state && `, ${student.state}`}
              {(student.pin_code || student.pincode) && ` - ${student.pin_code || student.pincode}`}
            </p>
          </div>

          {/* Documents */}
          <div className="mt-6 bg-white rounded-lg p-4 shadow-sm border">
            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Documents {documents.length > 0 && `(${documents.length})`}
            </h4>
            
            {/* Source Legend */}
            {documents.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-3 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold">A</span>
                  <span className="text-gray-600">Admission Form</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-purple-500 text-white flex items-center justify-center text-[10px] font-bold">P</span>
                  <span className="text-gray-600">Student Portal</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-orange-500 text-white flex items-center justify-center text-[10px] font-bold">O</span>
                  <span className="text-gray-600">Online Application</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-green-500 text-white flex items-center justify-center text-[10px] font-bold">✓</span>
                  <span className="text-gray-600">Verified</span>
                </span>
              </div>
            )}
            
            {documents.length > 0 ? (
              <div className="flex flex-wrap gap-4">
                {documents.map((doc, idx) => (
                  <DocumentThumbnailWithSource key={idx} doc={doc} onZoom={onZoom} />
                ))}
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <p className="text-yellow-700 text-sm font-medium">No documents uploaded</p>
                <p className="text-yellow-600 text-xs mt-1">Student has not uploaded any documents yet</p>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

const StudentRegister = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ courses: [], sessions: [] });
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  
  // Document zoom modal
  const [zoomDoc, setZoomDoc] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCourse) params.append('course_id', selectedCourse);
      if (selectedSession) params.append('session', selectedSession);
      if (selectedGender) params.append('gender', selectedGender);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      const response = await api.get(`/students/register/full-data?${params.toString()}`);
      setStudents(response.data.students || []);
      setTotalItems(response.data.total || 0);
      setFilters(response.data.filters || { courses: [], sessions: [] });
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load student register');
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, selectedSession, selectedGender, searchTerm, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const toggleRow = (studentId) => {
    setExpandedRows(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStudents();
  };

  const clearFilters = () => {
    setSelectedCourse('');
    setSelectedSession('');
    setSelectedGender('');
    setSearchTerm('');
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      toast.info('Fetching all data for export...');
      
      // Fetch all students without pagination for export
      const params = new URLSearchParams();
      if (selectedCourse) params.append('course_id', selectedCourse);
      if (selectedSession) params.append('session', selectedSession);
      if (selectedGender) params.append('gender', selectedGender);
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '10000'); // Get all records
      
      const response = await api.get(`/students/register/full-data?${params.toString()}`);
      const allStudents = response.data.students || [];
      
      if (allStudents.length === 0) {
        toast.error('No data to export');
        return;
      }

      const exportData = allStudents.map((s, index) => ({
        'SL': index + 1,
        'Student ID': s.student_id || s.admission_number || '-',
        'Full Name': s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim(),
        'Father Name': s.father_name || '-',
        'Mother Name': s.mother_name || '-',
        'Phone': s.phone || s.mobile || '-',
        'Email': s.email || '-',
        'Gender': s.gender || '-',
        'Date of Birth': s.date_of_birth || '-',
        'Course': s.course_name || '-',
        'Session': s.session || '-',
        'Admission Date': s.admission_date || '-',
        'Address': s.address || s.present_address || '-',
        'City': s.city || '-',
        'State': s.state || '-',
        'PIN': s.pin_code || s.pincode || '-',
        'Father Phone': s.father_phone || '-',
        'Father Occupation': s.father_occupation || '-',
        'Mother Phone': s.mother_phone || '-',
        'Mother Occupation': s.mother_occupation || '-',
        '10th Board': s.class_10_board || '-',
        '10th School': s.class_10_school || '-',
        '10th Year': s.class_10_year || '-',
        '10th %': s.class_10_percentage || '-',
        '12th Board': s.class_12_board || '-',
        '12th School': s.class_12_school || '-',
        '12th Year': s.class_12_year || '-',
        '12th %': s.class_12_percentage || '-',
        'Consultant Name': s.consultant_name || '-',
        'Consultant Code': s.consultant_code || '-',
        'Consultant Phone': s.consultant_phone || '-',
        'Status': s.is_approved ? 'Approved' : 'Pending'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Student Register');
      XLSX.writeFile(wb, `Student_Register_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(`Excel file downloaded with ${allStudents.length} records!`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export data');
    }
  };

  return (
    <div className="space-y-6" data-testid="student-register-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Student Register</h1>
          <p className="text-gray-600 mt-1">Complete student information with documents</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchStudents} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by name, ID, phone, father name..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Button type="submit" disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button 
              type="button" 
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </form>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Course</label>
                <select
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  data-testid="course-filter"
                >
                  <option value="">All Courses</option>
                  {filters.courses.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Session</label>
                <select
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  data-testid="session-filter"
                >
                  <option value="">All Sessions</option>
                  {filters.sessions.map(s => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Gender</label>
                <select
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={selectedGender}
                  onChange={(e) => setSelectedGender(e.target.value)}
                  data-testid="gender-filter"
                >
                  <option value="">All</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters} className="w-full">
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-blue-600 text-sm font-medium">Total Students</p>
            <p className="text-2xl font-bold text-blue-700">{totalItems}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-green-600 text-sm font-medium">On This Page</p>
            <p className="text-2xl font-bold text-green-700">{students.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-pink-50 border-pink-200">
          <CardContent className="p-4">
            <p className="text-pink-600 text-sm font-medium">Male (Page)</p>
            <p className="text-2xl font-bold text-pink-700">
              {students.filter(s => s.gender === 'Male').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <p className="text-purple-600 text-sm font-medium">Approved</p>
            <p className="text-2xl font-bold text-purple-700">
              {students.filter(s => s.is_approved).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Students Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading students...</span>
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No students found</p>
              <p className="text-sm mt-1">Try adjusting your filters or search term</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="student-register-table">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Photo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Father Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Course</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Session</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Admission Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Consultant</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {students.map((student, index) => {
                    // Get photo using the improved getStudentPhoto function
                    const studentPhoto = getStudentPhoto(student);
                    
                    return (
                    <React.Fragment key={student.id}>
                      <tr 
                        className={`hover:bg-blue-50 cursor-pointer transition-colors ${expandedRows[student.id] ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleRow(student.id)}
                        data-testid={`student-row-${student.id}`}
                      >
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            {expandedRows[student.id] ? (
                              <ChevronDown className="w-4 h-4 text-blue-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {studentPhoto ? (
                            <img 
                              src={studentPhoto} 
                              alt="" 
                              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-blue-600">
                          {student.student_id || student.admission_number || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {student.father_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {student.phone || student.mobile || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {student.course_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {student.session || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {student.admission_date || student.created_at?.split('T')[0] || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {student.consultant_name ? (
                            <div className="text-xs">
                              <p className="font-medium text-purple-700">{student.consultant_name}</p>
                              {student.consultant_code && <p className="text-gray-500">Code: {student.consultant_code}</p>}
                              {student.consultant_phone && <p className="text-gray-500">{student.consultant_phone}</p>}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                      {expandedRows[student.id] && <ExpandedRowContent student={student} onZoom={setZoomDoc} />}
                    </React.Fragment>
                    );})}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {!loading && students.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalItems / itemsPerPage)}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Document Zoom Modal */}
      <Dialog open={!!zoomDoc} onOpenChange={() => { setZoomDoc(null); setZoomScale(1); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          {zoomDoc && (
            <div className="relative">
              {/* Header */}
              <div className="bg-gray-900 text-white px-4 py-3 flex items-center justify-between">
                <span className="font-medium">{zoomDoc.label}</span>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-white hover:bg-gray-700"
                    onClick={() => setZoomScale(s => Math.max(0.5, s - 0.25))}
                  >
                    -
                  </Button>
                  <span className="text-sm">{Math.round(zoomScale * 100)}%</span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-white hover:bg-gray-700"
                    onClick={() => setZoomScale(s => Math.min(3, s + 0.25))}
                  >
                    +
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-white hover:bg-gray-700 ml-2"
                    onClick={() => { setZoomDoc(null); setZoomScale(1); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Content */}
              <div className="bg-gray-800 p-4 overflow-auto max-h-[75vh] flex items-center justify-center">
                {zoomDoc.isImage ? (
                  <img 
                    src={zoomDoc.url} 
                    alt={zoomDoc.label}
                    style={{ transform: `scale(${zoomScale})`, transition: 'transform 0.2s' }}
                    className="max-w-full"
                  />
                ) : zoomDoc.isPdf ? (
                  <iframe 
                    src={zoomDoc.url} 
                    className="w-full h-[70vh] bg-white"
                    title={zoomDoc.label}
                  />
                ) : (
                  <div className="text-white text-center py-10">
                    <FileText className="w-16 h-16 mx-auto mb-4" />
                    <p>Preview not available</p>
                    <a 
                      href={zoomDoc.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline mt-2 block"
                    >
                      Open in new tab
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentRegister;
